import React, { useState, useEffect } from 'react'
import {
  Autocomplete,
  createFilterOptions,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'

const SearchableDropDown = ({
  placeholder,
  menuItems,
  className = '',
  placeholderClassName = '',
  borderColor = '!border-[#FFCA84]',
  value,
  setValue,
  selectedItemClassName = '',
  renderIcon,
  renderItem,
  shouldRenderItemSelected = true,
  searchKeys = ['label'],
  autoComplete = false,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState(menuItems)
  const [isOpen, setIsOpen] = useState(false)

  const selectedOption = filteredItems.find((item) => item.value === value) || null
  // Update filtered items when search term changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(menuItems)
      return
    }

    const searchTermLower = searchTerm.toLowerCase()

    // Filter items based on search keys
    const filtered = menuItems.filter((item) => {
      return searchKeys.some((key) => {
        const fieldValue = item[key]
        // If the field is an object or doesn't exist, skip it
        if (!fieldValue || typeof fieldValue !== 'string') return false
        return fieldValue.toLowerCase().includes(searchTermLower)
      })
    })

    setFilteredItems(filtered)

    // Auto-select the first matching item if there's an exact match or only one result
    if (
      filtered.length === 1 ||
      filtered.some(
        (item) =>
          item.value.toLowerCase() === searchTermLower ||
          item.value.replace('+', '') === searchTermLower ||
          item.searchText.toLowerCase() === searchTermLower
      )
    ) {
      const bestMatch =
        filtered.find(
          (item) =>
            item.value.toLowerCase() === searchTermLower ||
            item.value.replace('+', '') === searchTermLower ||
            item.searchText.toLowerCase() === searchTermLower
        ) || filtered[0]

      setValue(bestMatch.value)
    }
  }, [searchTerm, menuItems, searchKeys, setValue])

  // Handle keyboard input directly in the select component
  const handleKeyDown = (event) => {
    // Don't handle keydown if dropdown is closed
    if (!isOpen) return

    // Ignore special keys like Tab, Arrow keys, etc.
    if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') {
      // If Backspace, remove the last character
      if (event.key === 'Backspace') {
        setSearchTerm((prev) => prev.slice(0, -1))
      }
      // If it's a single character, append it to the search
      else if (event.key.length === 1) {
        setSearchTerm((prev) => prev + event.key)
      }
      // Don't close the dropdown on typing
      event.stopPropagation()
    }
  }

  const handleChange = (event) => {
    setValue(event.target.value)
    setSearchTerm('') // Clear search term after selection
  }

  // Track dropdown open state and clear search term when dropdown opens
  const handleOpen = () => {
    setIsOpen(true)
    setSearchTerm('')
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  // Handle direct input change without dropdown
  const handleDirectInput = (event) => {
    const input = event.target.value
    setSearchTerm(input)

    // Filter items based on input
    const filtered = menuItems.filter((item) => {
      return searchKeys.some((key) => {
        const fieldValue = item[key]
        if (!fieldValue || typeof fieldValue !== 'string') return false
        return fieldValue.toLowerCase().includes(input.toLowerCase())
      })
    })

    // Auto-select first match
    if (filtered.length > 0) {
      setValue(filtered[0].value)
    }
  }

  return (
    <FormControl
      fullWidth
      className={`dropdownMenuView2 !border-[1px] !border-solid ${borderColor} focus:!border-[#f67443] focus:ring-[#F67443] rounded-lg bg-white ${className}`}
    >
      {!autoComplete ? (
        <Select
          IconComponent={
            renderIcon ||
            (() => (
              <div aria-hidden="true" className="">
                <img width={10} height={10} src="/assets/image/up_down.svg" alt="" />
              </div>
            ))
          }
          id="searchable-select"
          value={value}
          displayEmpty
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          onKeyDown={handleKeyDown}
          input={
            <OutlinedInput
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              }}
              onChange={handleDirectInput}
            />
          }
          renderValue={(selected) => {
            if (!selected) {
              return <span className={`text-[#F67443] ${placeholderClassName}`}>{placeholder}</span>
            }

            if (renderItem && shouldRenderItemSelected) {
              return renderItem(menuItems.find((e) => e.value === value))
            }

            // For country codes, display just the dial code
            const selectedItem = menuItems.find((e) => e.value === value)
            return (
              <span className={`text-[#F67443] ${selectedItemClassName}`}>
                {selectedItem ? selectedItem.value : value}
              </span>
            )
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
          {...props}
        >
          {searchTerm && (
            <MenuItem disabled className="text-gray-500 italic text-sm">
              Searching: {searchTerm}
            </MenuItem>
          )}

          {filteredItems.length > 0 ? (
            filteredItems.map((menuItem, index) => (
              <MenuItem
                className="!text-[#F67443]"
                key={menuItem.key || `${menuItem.value}_${index}`}
                value={menuItem.value}
              >
                {renderItem ? renderItem(menuItem) : menuItem.displayLabel || menuItem.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No matches found</MenuItem>
          )}
        </Select>
      ) : (
        <Autocomplete
          disablePortal
          // disableClearable
          popupIcon={
            // <div aria-hidden="true">
            <img
              width={'20px'}
              className=" h-full w-2 block shrink-0 mr-0  top-1/2 right-2 "
              style={{ display: selectedOption ? 'none' : 'block' }}
              height={10}
              src="/assets/image/up_down.svg"
              alt=""
            />
            // </div>
          }
          options={filteredItems || []}
          renderValue={(selected, getItemProps) => {
            if (!selected) {
              return <span className={`text-[#F67443] ${placeholderClassName}`}>{placeholder}</span>
            }
            const itemProps = getItemProps()
            const selectedItem = menuItems.find((e) => e.value === value)

            if (renderItem && shouldRenderItemSelected) {
              return (
                <span
                  {...itemProps}
                  className=" w-full"
                  style={{ outline: 'none', boxShadow: 'none' }}
                >
                  {renderItem(selectedItem)}
                </span>
              )
            }

            // For country codes, display just the dial code
            return (
              <span {...itemProps} className={`text-[#F67443] ${selectedItemClassName}`}>
                {selectedItem ? selectedItem?.value : value}
              </span>
            )
          }}
          // placeholder={placeholder}
          value={selectedOption}
          onChange={(event, newValue) => {
            console.log(newValue)
            setValue(newValue?.value)
          }}
          renderOption={(props, menuItem, { selected }) => (
            <li {...props}>
              {renderItem ? renderItem(menuItem) : menuItem.displayLabel || menuItem.label}
            </li>
          )}
          getOptionLabel={(option) =>
            option.searchText || option.label || option.displayLabel || option.value
          } // 👈 THIS
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder={!selectedOption ? placeholder : ''}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '4px 8px',
                  caretColor: 'transparent',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& input::placeholder': {
                  color: '#F67443',
                },
                '& .MuiAutocomplete-input': {
                  textAlign: 'left',
                  paddingLeft: '0px',
                },
              }}
            />
          )}
        />
      )}
    </FormControl>
  )
}

export default SearchableDropDown
